import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { Button, Icon, Label } from '../../components/widgets';
import withTranslate from '../../containers/widgets/withTranslate';
import { getUserData } from '../../selectors/user';
import { FEATURE_OPEN_REASON } from '../../utils/consts';
import {
    FEATURE_CONFIG,
    FEATURE_STATUS,
    FEATURE_STATUS_CONFIG,
    FEATURE_TYPE,
    getFeatureStatus,
    MIXPANEL_FEATURES_CONTACT_CLICK,
} from '../consts';
import AccountPlanAPI from '../service';
import Card from './Card';
import css from './Features.css';
import { ACCOUNT_PLAN_EVENT_PREFIX, trackMixpanelEvent } from '../../utils/general';

const accountPlanAPI = new AccountPlanAPI();

const Features = ({ translate, features = {} }) => {
    const { organization_tier: tier, is_unified: isUnified } = useSelector(getUserData);
    const [contactAddOnsClicked, setContactAddOnsClicked] = useState(false);
    const [contactAdvancedCollectClicked, setContactAdvancedCollectClicked] = useState(false);

    const formattedFeatures = FEATURE_CONFIG.filter(({ isVisible }) => isVisible(features, isUnified))
        .map(config => {
            const feature = features[config.name];
            return {
                ...config,
                title: translate(
                    `STATIC.PAGES.ACCOUNT_PLAN.FEATURES.${config.name}`,
                    config.renderTextReplace?.(features) || {}
                ),
                status: config.getStatus?.(features) || getFeatureStatus(feature),
            };
        })
        .sort((a, b) => {
            const orderDiff = FEATURE_STATUS_CONFIG[a.status].ORDER - FEATURE_STATUS_CONFIG[b.status].ORDER;
            if (orderDiff !== 0) return orderDiff;
            return a.title.localeCompare(b.title);
        });
    const addOnsFeatures = formattedFeatures.filter(({ type }) => type === FEATURE_TYPE.ADD_ONS);
    const advancedCollectFeatures = formattedFeatures.filter(({ type }) => type === FEATURE_TYPE.ADVANCED_COLLECT);

    const handleContactClick = type => {
        if (type === FEATURE_TYPE.ADD_ONS) {
            setContactAddOnsClicked(true);
        } else {
            setContactAdvancedCollectClicked(true);
        }
        trackMixpanelEvent(`${ACCOUNT_PLAN_EVENT_PREFIX}:`, MIXPANEL_FEATURES_CONTACT_CLICK, {
            type,
        });
        return accountPlanAPI.submitContactUs(tier);
    };

    const renderStatus = (status, withLabel = true) => (
        <div className={css.statusContainer}>
            {status === FEATURE_STATUS.TRIAL && withLabel ? (
                <Label
                    className={css.statusLabel}
                    text={`STATIC.PAGES.ACCOUNT_PLAN.FEATURE_STATUS.${status.toUpperCase()}`}
                    style={{
                        color: FEATURE_STATUS_CONFIG[status].STATUS_ICON_COLOR,
                    }}
                />
            ) : (
                <Icon
                    size={IconSizes.X_SMALL}
                    variant={status === FEATURE_STATUS.AVAILABLE ? IconVariants.LIGHT : IconVariants.SUCCESS}
                    name={status === FEATURE_STATUS.AVAILABLE ? 'shopping-cart' : 'check'}
                    interactive={false}
                />
            )}
        </div>
    );

    const getFeatureTitleStyle = status => ({
        color: FEATURE_STATUS_CONFIG[status].TITLE_COLOR,
    });

    const renderAddOn = ({ name, status, title, iconName, learnMoreLink }) => (
        <div key={name} className={css.addOnItem}>
            <div className={css.mainContainer}>
                <Icon
                    className={css.icon}
                    name={iconName}
                    interactive={false}
                    style={{
                        backgroundColor: FEATURE_STATUS_CONFIG[status].BACKGROUND_COLOR,
                        color: FEATURE_STATUS_CONFIG[status].FEATURE_ICON_COLOR,
                    }}
                />
                <div className={css.nameContainer}>
                    <Label text={title} className={css.addOnName} style={getFeatureTitleStyle(status)} />
                    {status === FEATURE_STATUS.AVAILABLE && learnMoreLink && (
                        <Button
                            type="link"
                            text="STATIC.PAGES.ACCOUNT_PLAN.BUTTONS.LEARN_MORE"
                            className={css.learnMoreButton}
                            contentClass={css.learnMoreButtonContent}
                            onClick={() => window.open(learnMoreLink, '_blank')}
                            Icon="external-link"
                            iconClass={css.learnMoreIcon}
                        />
                    )}
                </div>
            </div>
            {renderStatus(status)}
        </div>
    );

    const renderAdvancedCollect = ({ name, status, title }) => (
        <div key={name} className={css.advancedCollectItem}>
            {renderStatus(status, false)}
            <Label text={title} className={css.advancedCollectName} style={getFeatureTitleStyle(status)} />
        </div>
    );

    return (
        <>
            <Card
                header={
                    <>
                        <Label
                            className={css.title}
                            text="STATIC.PAGES.ACCOUNT_PLAN.ADD_ONS"
                            textReplace={{
                                value: addOnsFeatures.filter(({ status }) => status !== FEATURE_STATUS.AVAILABLE)
                                    .length,
                                total: addOnsFeatures.length,
                            }}
                        />
                        <div className={css.contactButtonContainer}>
                            {contactAddOnsClicked && (
                                <Label text="STATIC.PAGES.ACCOUNT_PLAN.BE_IN_TOUCH" className={css.label} />
                            )}
                            <Button
                                type="secondary"
                                text="STATIC.PAGES.ACCOUNT_PLAN.BUTTONS.SPEAK_TO_SALES"
                                onClick={() => handleContactClick(FEATURE_TYPE.ADD_ONS)}
                                showV={contactAddOnsClicked}
                                disabled={contactAddOnsClicked}
                                className={css.contactButton}
                            />
                        </div>
                    </>
                }
            >
                <div className={css.addOnsContainer}>{addOnsFeatures.map(renderAddOn)}</div>
            </Card>
            <Card
                header={
                    <>
                        <Label className={css.title} text="STATIC.PAGES.ACCOUNT_PLAN.ADVANCED_COLLECT" />
                        <div className={css.contactButtonContainer}>
                            {contactAdvancedCollectClicked && (
                                <Label text="STATIC.PAGES.ACCOUNT_PLAN.BE_IN_TOUCH" className={css.label} />
                            )}
                            <Button
                                type="secondary"
                                text="STATIC.PAGES.ACCOUNT_PLAN.BUTTONS.SPEAK_TO_SALES"
                                onClick={() => handleContactClick(FEATURE_TYPE.ADVANCED_COLLECT)}
                                showV={contactAdvancedCollectClicked}
                                disabled={contactAdvancedCollectClicked}
                                className={css.contactButton}
                            />
                        </div>
                    </>
                }
            >
                <div className={css.advancedCollectContainer}>{advancedCollectFeatures.map(renderAdvancedCollect)}</div>
            </Card>
        </>
    );
};

Features.propTypes = {
    translate: PropTypes.func,
    features: PropTypes.objectOf(
        PropTypes.shape({
            value: PropTypes.bool.isRequired,
            meta: PropTypes.shape({
                created: PropTypes.string.isRequired,
                modified: PropTypes.string.isRequired,
                is_contracted: PropTypes.bool.isRequired,
                is_manually_set: PropTypes.bool.isRequired,
                feature_open_reason: PropTypes.oneOf(Object.values(FEATURE_OPEN_REASON)),
                salesforce_feature_name: PropTypes.string.isRequired,
            }),
        })
    ),
};

export default withTranslate(Features);
